import React, {
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
} from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  CircularProgress,
  Typography,
  IconButton,
  Card,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  DialogTitle,
  Button,
  Dialog,
  DialogContent,
  Badge,
  Fade,
  Tooltip,
} from "@mui/material";
import { useTestNavigation } from "../utils/testNavigation";
import {
  Warning,
  Visibility,
  VisibilityOff,
  CheckRounded as CheckRoundedIcon,
  AccessTime as AccessTimeIcon,
  ZoomIn,
  ZoomOut,
} from "@mui/icons-material";
import TimerOutlinedIcon from "@mui/icons-material/TimerOutlined";
import StickyNote2Icon from "@mui/icons-material/StickyNote2";
import MockMcq from "./MockMcq";
import MockNoteCompletion from "./MockNoteCompletion";
import TextHighlighterWrapper from "./TextHighlighterWrapper";
import NotesModal from "./NotesModal";
import HtmlRenderer from "./HtmlRenderer";

const DEFAULT_PART_TIMES = { partA: 15 * 60, BC_COMBINED: 45 * 60 }; // seconds

/* ---------------------------------------------------------------
   Header Toggle Icon Component
--------------------------------------------------------------- */

const HeaderToggleIcon = ({ isHeaderVisible, onToggle }) => {
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        width: "100%",
        py: 1,
        bgcolor: "rgba(247, 247, 247, 0.95)",
        backdropFilter: "blur(10px)",
        borderBottom: "1px solid rgba(157, 34, 53, 0.1)",
        position: "sticky",
        top: 0,
        zIndex: 999,
      }}
    >
      <Tooltip
        title={isHeaderVisible ? "Hide Header" : "Show Header"}
        placement="bottom"
        arrow
      >
        <IconButton
          onClick={onToggle}
          sx={{
            background: "linear-gradient(135deg, #9D2235 0%, #7a182a 100%)",
            color: "white",
            width: 40,
            height: 40,
            boxShadow: "0px 4px 16px rgba(157, 34, 53, 0.3)",
            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
            "&:hover": {
              background: "linear-gradient(135deg, #7a182a 0%, #5d1220 100%)",
              transform: "translateY(-1px)",
              boxShadow: "0px 6px 20px rgba(157, 34, 53, 0.4)",
            },
            "&:active": {
              transform: "translateY(0px)",
            },
          }}
        >
          {isHeaderVisible ? (
            <VisibilityOff fontSize="small" />
          ) : (
            <Visibility fontSize="small" />
          )}
        </IconButton>
      </Tooltip>
    </Box>
  );
};

// Helper function to get all Part B question IDs for navigation chips (like ListeningSection)
function getPartBQuestionIds(readingData, rawReadingData = null) {
  // First try to get from raw data if available
  if (rawReadingData?.data?.readingTest?.oetReadingPart) {
    const partBData = rawReadingData.data.readingTest.oetReadingPart.find(
      (part) => part.part === "B",
    );
    if (partBData?.questions) {
      const questionIds = partBData.questions
        .map((q) => q.questionId)
        .sort((a, b) => a - b);
      return questionIds;
    }
  }

  // Check OET structure in main readingData
  if (readingData?.oetReadingPart) {
    const partBData = readingData.oetReadingPart.find(
      (part) => part.part === "B",
    );
    if (partBData?.questions) {
      const questionIds = partBData.questions
        .map((q) => q.questionId)
        .sort((a, b) => a - b);
      return questionIds;
    }
  }

  // Check standard structure
  if (readingData?.partB) {
    const allQuestionIds = [];
    if (readingData.partB.mcqQuestions) {
      allQuestionIds.push(
        ...readingData.partB.mcqQuestions.map((q) => q.question_id),
      );
    }
    if (readingData.partB.sentenceCompletionQuestions) {
      allQuestionIds.push(
        ...readingData.partB.sentenceCompletionQuestions.map(
          (q) => q.question_id,
        ),
      );
    }
    if (readingData.partB.noteCompletionQuestions) {
      allQuestionIds.push(
        ...readingData.partB.noteCompletionQuestions.map((q) => q.question_id),
      );
    }
    if (allQuestionIds.length > 0) {
      const sortedIds = allQuestionIds.sort(
        (a, b) => (parseInt(a) || 0) - (parseInt(b) || 0),
      );
      return sortedIds;
    }
  }

  // Default fallback: Return empty array if no data found
  return [];
}

/* ---------------------------------------------------------------
   Enhanced Gradient Countdown (Ring + Pie + Center Icon)
--------------------------------------------------------------- */
const EnhancedCountdown = ({
  size = 56,
  stroke = 5,
  progress = 0, // 0..100
  showPie = true, // show inside pie fill
  showRing = true, // show outer progress ring
  idSuffix = "reading",
}) => {
  const p = Math.min(100, Math.max(0, progress));
  const finished = p >= 100;

  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const dash = c;
  const offset = c * (1 - p / 100);

  // ------- PIE PATH (sector fill) -------
  const angle = (p / 100) * Math.PI * 2;
  const cx = size / 2;
  const cy = size / 2;
  const x = cx + r * Math.cos(-Math.PI / 2 + angle);
  const y = cy + r * Math.sin(-Math.PI / 2 + angle);
  const largeArc = angle > Math.PI ? 1 : 0;
  const piePath =
    p <= 0
      ? ""
      : `
    M ${cx} ${cy}
    L ${cx} ${cy - r}
    A ${r} ${r} 0 ${largeArc} 1 ${x} ${y}
    Z
  `;

  return (
    <Box sx={{ position: "relative", width: size, height: size }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <defs>
          <linearGradient id={`grad-${idSuffix}`} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#9D2235" />
            <stop offset="100%" stopColor="#5d1220" />
          </linearGradient>
          <radialGradient id={`soft-${idSuffix}`} cx="50%" cy="50%" r="60%">
            <stop offset="0%" stopColor="rgba(157,34,53,0.10)" />
            <stop offset="100%" stopColor="rgba(157,34,53,0.0)" />
          </radialGradient>
        </defs>

        {/* Soft background plate for a card-ish look */}
        <circle cx={cx} cy={cy} r={r} fill={`url(#soft-${idSuffix})`} />

        {/* Track */}
        {showRing && (
          <circle
            cx={cx}
            cy={cy}
            r={r}
            fill="none"
            stroke="rgba(157,34,53,0.18)"
            strokeWidth={stroke}
          />
        )}

        {/* Pie fill from center (sector) */}
        {showPie && p > 0 && (
          <path d={piePath} fill={`url(#grad-${idSuffix})`} />
        )}

        {/* Progress Ring on top */}
        {showRing && (
          <circle
            cx={cx}
            cy={cy}
            r={r}
            fill="none"
            stroke={`url(#grad-${idSuffix})`}
            strokeWidth={stroke}
            strokeLinecap="round"
            strokeDasharray={`${dash} ${dash}`}
            strokeDashoffset={offset}
            transform={`rotate(-90 ${cx} ${cy})`}
          />
        )}

        {/* Subtle glow when finished */}
        {finished && (
          <circle
            cx={cx}
            cy={cy}
            r={r}
            fill="none"
            stroke="#9D2235"
            strokeOpacity="0.25"
            strokeWidth={2}
          />
        )}
      </svg>

      {/* Center icon */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          transition: "transform 180ms ease",
          transform: finished ? "scale(1.05)" : "scale(1)",
        }}
      >
        {finished ? (
          <CheckRoundedIcon
            sx={{ fontSize: Math.round(size * 0.34), color: "#7a182a" }}
          />
        ) : (
          <AccessTimeIcon
            sx={{ fontSize: Math.round(size * 0.34), color: "#7a182a" }}
          />
        )}
      </Box>
    </Box>
  );
};

export default function ReadingSection({
  readingData,
  rawReadingData, // Raw data for navigation chips
  answers,
  onAnswerChange,
  onAutoNext,
  instrOpen,
  timer = undefined, // minutes or "no-limit"
  mode = "simulation", // "practice" | "simulation"
  currentPart = "A", // "A" | "B" | "C"
  currentExtractIndex = 0, // 0‑based
  totalExtracts = 0, // supplied for part B, else 1,
  instructionsViewed = false, // Whether instructions have been viewed
  startTime = null, // ISO string from API
  endTime = null, // ISO string from API
  onBack = null, // Function to handle back navigation
  isLastQuestion = false, // Whether this is the last question in practice mode
  onFinish = null, // Function to handle finish action
  selectedParts = null, // Array of selected parts in practice mode for bullet display
  setCurrentPart: setCurrentPartProp, // for parent-state updates
  setCurrentExtractIndex: setCurrentExtractIndexProp, // for parent-state updates
  setAnswers: setAnswersProp, // for parent-state updates
  testId = null, // Test ID for browser back navigation
  // Notes props - now managed by parent component (same as ListeningSection)
  notes = [],
  onAddNote = null,
  notesModalOpen = false,
  setNotesModalOpen = null,
  onJumpToExtract = null,
}) {
  // ----- Zoom state for left (passage) and right (questions) panels -----
  const [leftZoom, setLeftZoom] = useState(1);
  const [rightZoom, setRightZoom] = useState(1);

  // Ref for right panel container (for dropdown positioning)
  const rightPanelRef = useRef(null);

  // Zoom functions
  const handleLeftZoomIn = () => {
    setLeftZoom((prev) => Math.min(prev + 0.1, 1.5));
  };
  const handleLeftZoomOut = () => {
    setLeftZoom((prev) => Math.max(prev - 0.1, 0.8));
  };
  const handleRightZoomIn = () => {
    setRightZoom((prev) => Math.min(prev + 0.1, 1.5));
  };
  const handleRightZoomOut = () => {
    setRightZoom((prev) => Math.max(prev - 0.1, 0.8));
  };

  // ----- part data -----
  const partKey = `part${currentPart}`;
  const part = useMemo(
    () => readingData?.[partKey] ?? {},
    [readingData, partKey],
  );
  const extracts = part.readingTexts ?? [];

  // Navigation hook and test type utilities
  const navigate = useNavigate();
  const { baseRoute, testType } = useTestNavigation();

  // Handle browser back button - navigate to main screen
  useEffect(() => {
    if (!testId) return; // Only if testId is provided

    const handlePopState = (event) => {
      // Prevent default back navigation
      event.preventDefault();
      // Navigate to main mock test screen using React Router
      navigate(`${baseRoute}/${testId}`, { replace: true });
    };

    // Add popstate event listener
    window.addEventListener("popstate", handlePopState);

    // Push current state to allow back button to trigger our handler
    window.history.pushState(null, "", window.location.pathname);

    // Cleanup
    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [testId, navigate, baseRoute]);

  // ----- Timer logic -----
  const customLimit =
    mode === "practice" && timer && timer !== "no-limit"
      ? Number(timer) * 60
      : null;

  const initSecs =
    customLimit ??
    (currentPart === "A"
      ? DEFAULT_PART_TIMES.partA
      : DEFAULT_PART_TIMES.BC_COMBINED);

  // Initialize remaining time from API endTime or custom limit
  const getInitialRemaining = () => {
    // For API timer mode (with endTime), calculate remaining from API
    if (endTime) {
      const now = new Date();
      const end = new Date(endTime);
      const remainingMs = end.getTime() - now.getTime();
      const remainingSecs = Math.max(0, Math.floor(remainingMs / 1000));
      console.log("Initializing timer from API endTime:", {
        endTime,
        remainingSecs,
        currentPart,
      });
      return remainingSecs;
    }
    // For custom limit (user-selected timer)
    return initSecs;
  };

  const [remaining, setRemaining] = useState(getInitialRemaining);
  const tickRef = useRef(null);
  const startedRef = useRef(false);
  const prevPartRef = useRef(currentPart);
  // Track Part B/C as a combined group
  const prevPartGroupRef = useRef(currentPart === "A" ? "A" : "BC");

  // Timer expired (part-level)
  const [timerExpired, setTimerExpired] = useState(false);
  const [expiryCountdown, setExpiryCountdown] = useState(10);
  const [expiryHandled, setExpiryHandled] = useState(false);

  // For API-timer (backend start/end)
  const [apiTimeRemaining, setApiTimeRemaining] = useState(0);
  const [timerPaused, setTimerPaused] = useState(false);

  // Determine selected parts from the selectedParts prop (array of part letters like ["A", "B", "C"])
  // This is used for determining timer behavior
  const selectedPartsArray = useMemo(() => {
    if (!selectedParts) return ["A", "B", "C"]; // Default: all parts
    if (Array.isArray(selectedParts)) {
      // Check if it's array of objects with part property or just letters
      if (selectedParts.length > 0 && typeof selectedParts[0] === "object") {
        return selectedParts.map((p) => p.part).filter(Boolean);
      }
      return selectedParts;
    }
    return ["A", "B", "C"];
  }, [selectedParts]);

  // Handle timer expiry based on selected parts and current part
  const handleTimerExpire = useCallback(() => {
    if (!expiryHandled) {
      setExpiryHandled(true);
      setTimerExpired(false);

      // Logic based on which part's timer expired:
      // Part A timer expires → always move to next part (B or C)
      // Part B/C combined timer expires → always submit test

      if (currentPart === "A") {
        // Part A timer expired - move to next part
        if (onAutoNext) {
          console.log("Part A timer expired - moving to next part");
          onAutoNext();
        }
      } else {
        // Part B or C timer expired (combined timer) - submit test
        console.log("Part B/C combined timer expired - submitting test");
        if (onFinish) {
          onFinish(true); // true indicates timer expiry
        }
      }
    }
  }, [expiryHandled, currentPart, onAutoNext, onFinish]);

  // Timer expiry effect: start countdown and auto-advance unless user clicks Continue
  useEffect(() => {
    if (timerExpired && !expiryHandled) {
      setExpiryCountdown(10);
      const interval = setInterval(() => {
        setExpiryCountdown((s) => {
          if (s <= 1) {
            clearInterval(interval);
            handleTimerExpire();
            return 10;
          }
          return s - 1;
        });
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [timerExpired, expiryHandled, handleTimerExpire]);

  // "Continue" button handler for timer modal
  const handleTimerContinue = () => {
    if (!expiryHandled) {
      handleTimerExpire();
    }
  };

  // Reset expiryHandled for new part (not every extract)
  useEffect(() => {
    setExpiryHandled(false);
    setTimerExpired(false);
  }, [currentPart]);

  // Track the time when instructions are opened
  const [instructionOpenTime, setInstructionOpenTime] = useState(null);
  const [totalInstructionTime, setTotalInstructionTime] = useState(0);

  // Check for extract change when component mounts or updates
  useEffect(() => {
    const extractChangeData = window.sessionStorage.getItem(
      "reading-extract-changing",
    );
    if (extractChangeData) {
      // Extract change is happening, but we don't need to track state for now
    }
  }, [instrOpen]);

  // Detect when instructions are opened and closed
  useEffect(() => {
    // When instructions are opened
    if (instrOpen && !instructionOpenTime) {
      setInstructionOpenTime(Date.now());
    }
    // When instructions are closed
    else if (!instrOpen && instructionOpenTime) {
      const timeSpentOnInstructions = Math.floor(
        (Date.now() - instructionOpenTime) / 1000,
      );

      // Add to running total of instruction time
      const newTotal = totalInstructionTime + timeSpentOnInstructions;
      setTotalInstructionTime(newTotal);

      // Get any extract change data that might be relevant
      const extractChangeData = window.sessionStorage.getItem(
        "reading-extract-changing",
      );
      const isChangingExtract = !!extractChangeData;

      // If we have an end time, adjust it to compensate for instruction time
      if (endTime) {
        // Adjust by the time spent in this instruction modal
        // For extract changes, we need to add at least 10 seconds to avoid the timer issue
        const compensationTime = isChangingExtract
          ? Math.max(10, timeSpentOnInstructions) * 1000
          : timeSpentOnInstructions * 1000;

        const adjustedEndTime = new Date(
          new Date(endTime).getTime() + compensationTime,
        );

        // We need to communicate this adjusted end time to parent component
        window.dispatchEvent(
          new CustomEvent("endTimeAdjusted", {
            detail: {
              adjustedEndTime: adjustedEndTime.toISOString(),
              timeSpentOnInstructions,
              totalInstructionTime: newTotal,
              isExtractChange: isChangingExtract,
            },
          }),
        );

        // Clear extract change data from session storage
        if (isChangingExtract) {
          window.sessionStorage.removeItem("reading-extract-changing");
        }
      }

      setInstructionOpenTime(null);
    }
  }, [instrOpen, instructionOpenTime, endTime, totalInstructionTime]);

  // Main timer or API-timer watcher (part-level only)
  useEffect(() => {
    // Don't start timer if instructions haven't been viewed yet or are currently open
    if (!instructionsViewed || instrOpen) {
      return;
    }

    // If we have an API endTime, don't run the customLimit timer - use API timer instead
    if (endTime) {
      return; // API timer will handle everything
    }

    // Determine current part group (A is separate, B/C are combined)
    const currentPartGroup = currentPart === "A" ? "A" : "BC";

    // Check if we're switching between part groups (A <-> BC)
    const switchingPartGroups = currentPartGroup !== prevPartGroupRef.current;

    if (customLimit) {
      // Reset startedRef when switching between part groups so new timer can start
      if (switchingPartGroups) {
        startedRef.current = false;
        clearInterval(tickRef.current);
        tickRef.current = null;
        prevPartGroupRef.current = currentPartGroup;
      }

      if (!startedRef.current) {
        startedRef.current = true;
        clearInterval(tickRef.current);
        tickRef.current = setInterval(() => {
          setRemaining((r) => {
            if (r <= 1) {
              clearInterval(tickRef.current);
              tickRef.current = null;
              setTimerExpired(true);
              setExpiryHandled(false);
              return 0;
            }
            return r - 1;
          });
        }, 1000);

        // Always use customLimit for fresh timer - no sessionStorage restoration
        setRemaining(customLimit);
      }
    }

    if (!customLimit) {
      const secs =
        currentPart === "A"
          ? DEFAULT_PART_TIMES.partA
          : DEFAULT_PART_TIMES.BC_COMBINED;

      // Group Part B and C together for timer purposes
      const currentPartGroup = currentPart === "A" ? "A" : "BC";

      // Only reset timer when switching between Part A and Parts B/C (not between B and C)
      if (!tickRef.current || currentPartGroup !== prevPartGroupRef.current) {
        clearInterval(tickRef.current);
        tickRef.current = setInterval(() => {
          setRemaining((r) => {
            if (r <= 1) {
              clearInterval(tickRef.current);
              tickRef.current = null;
              setTimerExpired(true);
              setExpiryHandled(false);
              return 0;
            }
            return r - 1;
          });
        }, 1000);
        setRemaining(secs);
      }
      prevPartRef.current = currentPart;
      prevPartGroupRef.current = currentPartGroup;
      return () => clearInterval(tickRef.current);
    }
  }, [customLimit, instrOpen, currentPart, instructionsViewed]);

  // API timer (from backend-provided start/end)
  useEffect(() => {
    // Don't start API timer if instructions haven't been viewed yet or are currently open
    if (!instructionsViewed || instrOpen) {
      return;
    }

    if (!endTime) return;

    // We'll use the endTime that was possibly adjusted by the instruction time compensation

    // Check if we need to adjust the timer due to instruction time
    const endDateTime = new Date(endTime);

    // We can add debug info to log the actual remaining time
    const now = new Date();
    const initialRemainingMs = endDateTime.getTime() - now.getTime();

    const updateApiTimer = () => {
      if (timerPaused) return;
      const now = new Date();
      const endDateTime = new Date(endTime);
      const remainingMs = endDateTime.getTime() - now.getTime();
      const remainingSeconds = Math.max(0, Math.floor(remainingMs / 1000));

      // Update the timer display
      setApiTimeRemaining(remainingSeconds);

      // Check for timer expiration
      if (remainingSeconds <= 0 && !timerExpired && !expiryHandled) {
        setTimerExpired(true);
        setExpiryHandled(false);
      }
    };
    updateApiTimer();
    const interval = setInterval(updateApiTimer, 1000);

    const handlePauseTimer = () => {
      setTimerPaused(true);
    };
    window.addEventListener("pauseTestTimers", handlePauseTimer);

    return () => {
      clearInterval(interval);
      window.removeEventListener("pauseTestTimers", handlePauseTimer);
    };
  }, [
    endTime,
    timerPaused,
    timerExpired,
    expiryHandled,
    instructionsViewed,
    instrOpen,
  ]);

  // Calculate time for display
  let displayMins, displaySecs;
  const hasApiTimer = !!endTime;
  const isNoLimit = mode === "practice" && timer === "no-limit" && !hasApiTimer;

  if (isNoLimit) {
    displayMins = "--";
    displaySecs = "--";
  } else {
    const endTimeFromProps = endTime;
    if (endTimeFromProps) {
      displayMins = String(Math.floor(apiTimeRemaining / 60)).padStart(2, "0");
      displaySecs = String(apiTimeRemaining % 60).padStart(2, "0");
    } else {
      displayMins = String(Math.floor(remaining / 60)).padStart(2, "0");
      displaySecs = String(remaining % 60).padStart(2, "0");
    }
  }
  const mins = displayMins;
  const secs = displaySecs;

  // ----- Timer progress calculation -----
  const getTimerProgress = () => {
    if (isNoLimit) return 0;

    const endTimeFromProps = endTime;
    if (endTimeFromProps) {
      // API-based timer
      if (startTime) {
        const totalSeconds = Math.max(
          1,
          Math.floor(
            (new Date(endTimeFromProps).getTime() -
              new Date(startTime).getTime()) /
              1000,
          ),
        );
        const elapsedSeconds = totalSeconds - apiTimeRemaining;
        return Math.min(
          100,
          Math.max(0, (elapsedSeconds / totalSeconds) * 100),
        );
      }
      return 0;
    } else {
      // Local timer
      const totalSeconds = initSecs;
      const elapsedSeconds = totalSeconds - remaining;
      return Math.min(100, Math.max(0, (elapsedSeconds / totalSeconds) * 100));
    }
  };

  const timerProgress = getTimerProgress();

  // ----- bullet nav data -----
  const chipStates = useMemo(() => {
    let labels = [];
    let questionIds = [];

    if (currentPart === "A") {
      labels = Array.from({ length: 20 }, (_, i) => 1 + i);
      // For Part A, get the actual question IDs
      if (part.mcqQuestions) {
        questionIds = part.mcqQuestions.map((q) => q.question_id);
      }
    } else if (currentPart === "B") {
      // For Part B, show ALL questions from ALL extracts (not just current extract)
      const allPartBQuestionIds = getPartBQuestionIds(
        readingData,
        rawReadingData,
      );
      questionIds = allPartBQuestionIds;
      labels = allPartBQuestionIds; // Use actual question IDs as labels for Part B
    } else {
      const start = 27 + currentExtractIndex * 8;
      labels = Array.from({ length: 8 }, (_, i) => start + i);
      // For Part C, get the actual question IDs
      if (part.mcqQuestions) {
        questionIds = part.mcqQuestions.map((q) => q.question_id);
      }
      if (part.sentenceCompletionQuestions) {
        questionIds.push(
          ...part.sentenceCompletionQuestions.map((q) => q.question_id),
        );
      }
      if (part.noteCompletionQuestions) {
        questionIds.push(
          ...part.noteCompletionQuestions.map((q) => q.question_id),
        );
      }
    }

    if (mode === "practice") {
      const existingQuestionIds = [];
      if (part.mcqQuestions) {
        part.mcqQuestions.forEach((q) =>
          existingQuestionIds.push(q.question_id),
        );
      }
      if (part.sentenceCompletionQuestions) {
        part.sentenceCompletionQuestions.forEach((q) =>
          existingQuestionIds.push(q.question_id),
        );
      }
      if (part.noteCompletionQuestions) {
        part.noteCompletionQuestions.forEach((q) =>
          existingQuestionIds.push(q.question_id),
        );
      }
      if (existingQuestionIds.length > 0 && currentPart !== "B") {
        // For Part A and C, use existing question IDs in practice mode
        labels = existingQuestionIds;
        questionIds = existingQuestionIds;
      } else if (currentPart === "B") {
        // For Part B, always show all questions regardless of practice mode
        const allPartBQuestionIds = getPartBQuestionIds(
          readingData,
          rawReadingData,
        );
        questionIds = allPartBQuestionIds;
        labels = allPartBQuestionIds;
      }
    }

    return labels.map((label, index) => ({
      label,
      questionId: questionIds[index] || label,
      answered: Boolean(answers?.[questionIds[index] || label]),
    }));
  }, [
    currentPart,
    currentExtractIndex,
    answers,
    mode,
    part,
    readingData,
    rawReadingData,
  ]);

  // Highlight helper - now using TextHighlighterWrapper

  // next button label
  const getButtonText = () => {
    if (currentPart === "A") return "Next Part";
    if (currentPart === "B") {
      return currentExtractIndex < totalExtracts - 1
        ? "Next Extract"
        : "Next Part";
    }
    return currentExtractIndex === 0 ? "Next Extract" : "Next Section";
  };

  // question base number
  const questionBase =
    currentPart === "A"
      ? 1
      : currentPart === "B"
        ? 21
        : 27 + currentExtractIndex * 8; // part C (two blocks of 8)

  // Navigation confirmation modal
  const [showConfirm, setShowConfirm] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);

  // Header visibility state
  const [isHeaderVisible, setIsHeaderVisible] = useState(true);

  // Returns all question IDs for the current extract (MCQ, sentence, note)
  const getCurrentExtractQuestionIds = useCallback(() => {
    let ids = [];
    if (part.mcqQuestions?.length) {
      ids = ids.concat(part.mcqQuestions.map((q) => q.question_id));
    }
    if (part.sentenceCompletionQuestions?.length) {
      ids = ids.concat(
        part.sentenceCompletionQuestions.map((q) => q.question_id),
      );
    }
    if (part.noteCompletionQuestions?.length) {
      ids = ids.concat(part.noteCompletionQuestions.map((q) => q.question_id));
    }
    return ids;
  }, [part]);

  const handleNextClick = useCallback(() => {
    // 1) collect ONLY the current extract’s question IDs
    const ids = getCurrentExtractQuestionIds(); // this already exists above
    const anyUnattempted = ids.some((id) => {
      const v = answers?.[id];
      return v == null || String(v).trim() === "";
    });

    // 2) what to do after confirming (or when all answered)
    const proceed = () => {
      // Part B: go to next extract if any, else move on
      if (currentPart === "B") {
        const allPartBQuestionIds = getPartBQuestionIds(
          readingData,
          rawReadingData,
        );
        const isLastExtract =
          currentExtractIndex >= allPartBQuestionIds.length - 1;

        if (!isLastExtract) {
          window.scrollTo({ top: 0, behavior: "smooth" });
          if (typeof onJumpToExtract === "function") {
            onJumpToExtract("B", currentExtractIndex + 1); // works in both modes
          } else if (typeof setCurrentExtractIndexProp === "function") {
            // fallback for practice legacy
            setCurrentExtractIndexProp(currentExtractIndex + 1);
          }
          return;
        }
      }

      // Default: next part / finish
      window.scrollTo({ top: 0, behavior: "smooth" });
      if (mode === "practice" && isLastQuestion && onFinish) {
        onFinish();
      } else {
        onAutoNext?.();
      }
    };

    // 3) show confirm if anything unattempted
    if (anyUnattempted) {
      setPendingAction(() => proceed);
      setShowConfirm(true);
    } else {
      proceed();
    }
  }, [
    answers,
    currentPart,
    currentExtractIndex,
    isLastQuestion,
    mode,
    onAutoNext,
    onFinish,
    readingData,
    rawReadingData,
    onJumpToExtract, // <-- make sure this is in the deps
    setCurrentExtractIndexProp, // <-- fallback deps
  ]);

  const handleConfirmYes = () => {
    setShowConfirm(false);
    if (pendingAction) pendingAction();
  };
  const handleConfirmNo = () => {
    setShowConfirm(false);
    setPendingAction(null);
  };

  // Handle chip click to navigate to specific question
  const handleChipClick = (questionId) => {
    // --- Part B: jump to the extract that owns this question ---
    if (currentPart === "B") {
      const allPartBQuestionIds = getPartBQuestionIds(
        readingData,
        rawReadingData,
      );
      const questionIndex = allPartBQuestionIds.findIndex(
        (id) => String(id) === String(questionId),
      );

      if (questionIndex !== -1) {
        // Smooth scroll to top first
        window.scrollTo({ top: 0, behavior: "smooth" });

        // Prefer parent callback (handles SIMULATION via navigate; PRACTICE via setState)
        if (typeof onJumpToExtract === "function") {
          onJumpToExtract("B", questionIndex);
        } else {
          // Fallback: practice-only local state (keeps answers intact)
          setCurrentPartProp?.("B");
          setCurrentExtractIndexProp?.(questionIndex);
        }

        // After navigation/render, try to scroll the exact question into view & highlight
        setTimeout(() => {
          const el = document.querySelector(
            `[data-question-id="${questionId}"]`,
          );
          if (!el) {
            // If not ready yet, retry once a bit later
            setTimeout(() => {
              const el2 = document.querySelector(
                `[data-question-id="${questionId}"]`,
              );
              if (el2) {
                el2.scrollIntoView({ behavior: "smooth", block: "center" });
              }
            }, 1000);
            return;
          }

          el.scrollIntoView({ behavior: "smooth", block: "center" });

          // Professional highlight pulse
          const originalBackground = el.style.backgroundColor;
          const originalTransition = el.style.transition;
          const originalBoxShadow = el.style.boxShadow;

          el.style.transition = "all 0.3s ease-in-out";
          el.style.backgroundColor = "rgba(157, 34, 53, 0.08)";
          el.style.boxShadow =
            "0 0 0 3px rgba(157, 34, 53, 0.2), 0 4px 12px rgba(157, 34, 53, 0.15)";
          el.style.borderRadius = "8px";

          setTimeout(() => {
            el.style.backgroundColor = "rgba(157, 34, 53, 0.15)";
            el.style.boxShadow =
              "0 0 0 4px rgba(157, 34, 53, 0.3), 0 6px 16px rgba(157, 34, 53, 0.2)";
          }, 150);

          setTimeout(() => {
            el.style.backgroundColor = "rgba(157, 34, 53, 0.05)";
            el.style.boxShadow =
              "0 0 0 2px rgba(157, 34, 53, 0.1), 0 2px 8px rgba(157, 34, 53, 0.1)";
          }, 400);

          setTimeout(() => {
            el.style.transition = originalTransition;
            el.style.backgroundColor = originalBackground;
            el.style.boxShadow = originalBoxShadow;
            el.style.borderRadius = "";
          }, 1000);
        }, 500);

        return;
      }
    }

    // --- Part A & C: just scroll to the question & highlight it ---
    const element = document.querySelector(
      `[data-question-id="${questionId}"]`,
    );
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "center" });

      const originalBackground = element.style.backgroundColor;
      const originalTransition = element.style.transition;
      const originalBoxShadow = element.style.boxShadow;

      element.style.transition = "all 0.3s ease-in-out";
      element.style.backgroundColor = "rgba(157, 34, 53, 0.08)";
      element.style.boxShadow =
        "0 0 0 3px rgba(157, 34, 53, 0.2), 0 4px 12px rgba(157, 34, 53, 0.15)";
      element.style.borderRadius = "8px";

      setTimeout(() => {
        element.style.backgroundColor = "rgba(157, 34, 53, 0.15)";
        element.style.boxShadow =
          "0 0 0 4px rgba(157, 34, 53, 0.3), 0 6px 16px rgba(157, 34, 53, 0.2)";
      }, 150);

      setTimeout(() => {
        element.style.backgroundColor = "rgba(157, 34, 53, 0.05)";
        element.style.boxShadow =
          "0 0 0 2px rgba(157, 34, 53, 0.1), 0 2px 8px rgba(157, 34, 53, 0.1)";
      }, 400);

      setTimeout(() => {
        element.style.transition = originalTransition;
        element.style.backgroundColor = originalBackground;
        element.style.boxShadow = originalBoxShadow;
        element.style.borderRadius = "";
      }, 1000);
    }
  };

  // Custom Back Button Logic for Part B
  // Back button logic: show on all extracts except first in B/C (any mode)
  // ---- Back button logic (shows in all modes except on first extract of B/C) ----
  const showBackButton = useMemo(() => {
    if (currentPart === "B" && currentExtractIndex > 0) return true;
    if (currentPart === "C" && currentExtractIndex >= 0) return true; // Show back button on Part C extract 1 to go back to Part B
    return false;
  }, [currentPart, currentExtractIndex]);

  const handleBackClick = () => {
    if (onBack) {
      // Special case: Part C extract 1 should go back to Part B
      if (currentPart === "C" && currentExtractIndex === 0) {
        onBack(-1, currentPart); // -1 indicates going back to previous part
      } else if (currentPart === "B" && currentExtractIndex > 0) {
        // Simulation mode: decrement extract index within Part B
        onBack(currentExtractIndex - 1, currentPart);
      } else {
        // Regular back navigation within the same part
        onBack(currentExtractIndex - 1, currentPart);
      }
    }
  };

  // --------------- B/C Extract Navigation UI -------------------
  // Only render if parent provided these setters (for parent state)
  const canJumpNav =
    !!setCurrentPartProp && !!setCurrentExtractIndexProp && !!setAnswersProp;

  // Get extract counts for B and C for navigation
  const partBExtractCount = readingData?.partB?.readingTexts?.length || 0;
  const partCExtractCount = readingData?.partC?.readingTexts?.length || 0;

  // =================== UI ===================
  return (
    <Box sx={{ height: "100vh", display: "flex", flexDirection: "column" }}>
      {/* ===== Header ===== */}
      <Box
        sx={{
          width: "100%",
          maxWidth: "100%",
          bgcolor: "#F7F7F7",
          px: { xs: 0.5, sm: 0.5, md: 0.5, lg: 0.5 },
          py: { xs: 0.1 },
          position: "sticky",
          top: 0,
          zIndex: 1000,
          display: isHeaderVisible ? "block" : "none",
          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
          borderBottom: "1px solid #e0e0e0",
        }}
      >
        <Card
          elevation={1}
          sx={{
            width: "100%",
            mb: 1,
            p: { xs: 0.2, sm: 0.8, md: 1, lg: 1.2 },
            bgcolor: "#fff",
            borderRadius: { xs: 0.8, sm: 1.5, md: 2 },
            boxShadow: "0px 4px 12px rgba(0,0,0,0.05)",
          }}
        >
          <Box
            sx={{
              display: "grid",
              alignItems: "center",
              gridTemplateAreas: {
                xs: `"left" "center" "right"`,
                sm: `"left center right"`,
                md: `"left center right"`,
                lg: `"left center right"`,
              },
              gridTemplateColumns: {
                xs: "1fr",
                sm: "1fr 2fr 1fr",
                md: "1fr 2fr 1fr",
                lg: "1fr 2fr 1fr",
              },
              gridTemplateRows: { xs: "auto auto auto", sm: "auto" },
              columnGap: { xs: 0, sm: 0.8, md: 1.2, lg: 1.5 },
              rowGap: { xs: 0.3, sm: 0 },
              minWidth: 0,
              minHeight: { xs: 20, sm: 45, md: 50 },
            }}
          >
            {/* LEFT - Reading Info */}
            <Box
              sx={{
                gridArea: "left",
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                width: "100%",
                gap: { xs: 0.3, sm: 1, md: 1.2, lg: 1.5 },
                py: { xs: 0.1, sm: 0.2 },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                  minWidth: 0,
                  flexShrink: 1,
                  width: "100%",
                }}
              >
                <Typography
                  sx={{
                    fontWeight: 700,
                    color: "#9D2235",
                    fontSize: { xs: "0.6rem", sm: "0.85rem", md: "0.95rem" },
                    lineHeight: 1.1,
                    whiteSpace: "nowrap",
                  }}
                >
                  Reading Module
                </Typography>

                <Typography
                  sx={{
                    fontWeight: 700,
                    color: "#7a7a7a",
                    fontSize: { xs: "0.55rem", sm: "0.76rem", md: "0.8rem" },
                    lineHeight: 1.1,
                    letterSpacing: 0.1,
                    whiteSpace: { xs: "normal", sm: "nowrap", md: "normal" },
                  }}
                >
                  <b>
                    Part ({currentPart}) Extract {currentExtractIndex + 1} of{" "}
                    {totalExtracts || extracts.length || 1}
                  </b>
                </Typography>
              </Box>
            </Box>

            {/* CENTER - Question Navigation Chips */}
            <Box
              sx={{
                gridArea: "center",
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                alignItems: "center",
                gap: { xs: 0.2, sm: 0.8, md: 1.2, lg: 1.5 },
                minWidth: 0,
                justifyContent: "center",
                py: { xs: 0.1, sm: 0.3, md: 0.4 },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  flexWrap: "wrap",
                  px: 0,
                  py: 0,
                  minWidth: { xs: "100%", sm: "auto" },
                  justifyContent: "center", // Changed from flex-start to center for all screen sizes
                }}
              >
                <Box
                  sx={{
                    display: "inline-flex",
                    gap: { xs: 0.2, sm: 0.3, md: 0.4, lg: 0.5 },
                    flexWrap: "wrap",
                    justifyContent: "center",
                  }}
                >
                  {chipStates.map(({ label, questionId, answered }) => (
                    <Box
                      key={label}
                      onClick={() => handleChipClick(questionId)}
                      sx={{
                        width: { xs: 18, sm: 22, md: 26, lg: 30 },
                        height: { xs: 18, sm: 22, md: 26, lg: 30 },
                        borderRadius: "50%",
                        border: "1.5px solid #9D2235",
                        bgcolor: answered ? "#9D2235" : "transparent",
                        color: answered ? "#fff" : "#9D2235",
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontWeight: 700,
                        fontSize: {
                          xs: "0.6rem",
                          sm: "0.7rem",
                          md: "0.8rem",
                          lg: "0.9rem",
                        },
                        cursor: "pointer",
                        transition: "background 0.15s",
                        "&:hover": {
                          bgcolor: answered
                            ? "#7a182a"
                            : "rgba(157,34,53,0.08)",
                        },
                        "&:active": {},
                        flex: "0 0 auto",
                      }}
                    >
                      {label}
                    </Box>
                  ))}
                </Box>
              </Box>
            </Box>

            {/* RIGHT - Timer and Notes */}
            <Box
              sx={{
                gridArea: "right",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: { xs: 0.3, sm: 0.6, md: 0.8 },
              }}
            >
              {/* Circle Clock - Above everything */}
              <Box sx={{ display: "flex", justifyContent: "center" }}>
                <EnhancedCountdown
                  size={20}
                  stroke={2}
                  progress={timerProgress}
                  showPie
                  showRing
                  idSuffix="readingTime"
                />
              </Box>

              {/* Timer and Notes Row */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: { xs: 0.4, sm: 0.8, md: 1 },
                }}
              >
                {/* Timer */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: { xs: 0.5, sm: 0.6, md: 0.7 },
                    px: { xs: 0.8, sm: 1, md: 1.2 },
                    py: { xs: 0.4, sm: 0.5, md: 0.6 },
                    borderRadius: { xs: 1.5, sm: 2, md: 2.5 },
                    minWidth: { xs: 70, sm: 90, md: 110 },
                    transition: "all 0.2s ease-in-out",
                  }}
                >
                  <AccessTimeIcon
                    sx={{
                      fontSize: { xs: 16, sm: 20, md: 24 },
                      color: "#9D2235",
                    }}
                  />
                  <Typography
                    sx={{
                      fontSize: { xs: "0.8rem", sm: "1rem", md: "1.2rem" },
                      fontWeight: 800,
                      color: "#9D2235",
                      fontFamily: "'Roboto Mono', 'Courier New', monospace",
                      letterSpacing: { xs: 0.4, sm: 0.6, md: 0.8 },
                      lineHeight: 1,
                    }}
                  >
                    {mins}:{secs}
                  </Typography>
                </Box>

                {/* Notes Icon */}
                <Badge
                  badgeContent={notes?.length || 0}
                  sx={{
                    "& .MuiBadge-badge": {
                      bgcolor: "#fff",
                      color: "#9D2235",
                      fontWeight: "bold",
                      fontSize: { xs: "0.35rem", sm: "0.45rem", md: "0.5rem" },
                      minWidth: { xs: 6, sm: 10, md: 12 },
                      height: { xs: 6, sm: 10, md: 12 },
                      boxShadow: "0 2px 4px rgba(157,34,53,0.2)",
                    },
                  }}
                >
                  <IconButton
                    onClick={() => setNotesModalOpen(true)}
                    sx={{
                      background:
                        "linear-gradient(135deg, #9D2235 0%, #7a182a 100%)",
                      color: "#fff",
                      border: "1px solid rgba(255,255,255,0.1)",
                      boxShadow:
                        "0 4px 12px rgba(157,34,53,0.25), 0 2px 4px rgba(157,34,53,0.15)",
                      transition: "all 0.2s ease-in-out",
                      "&:hover": {
                        background:
                          "linear-gradient(135deg, #7a182a 0%, #5d1220 100%)",
                        transform: "translateY(-1px)",
                        boxShadow:
                          "0 6px 16px rgba(157,34,53,0.3), 0 4px 8px rgba(157,34,53,0.2)",
                      },
                      width: { xs: 38, sm: 38, md: 38 },
                      height: { xs: 38, sm: 38, md: 38 },
                      minWidth: { xs: 38, sm: 38, md: 38 },
                      minHeight: { xs: 38, sm: 38, md: 38 },
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      padding: 0,
                    }}
                  >
                    <StickyNote2Icon
                      sx={{
                        fontSize: { xs: 18, sm: 18, md: 18 },
                        width: { xs: 18, sm: 18, md: 18 },
                        height: { xs: 18, sm: 18, md: 18 },
                        minWidth: { xs: 18, sm: 18, md: 18 },
                        minHeight: { xs: 18, sm: 18, md: 18 },
                        filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.3))",
                      }}
                    />
                  </IconButton>
                </Badge>
              </Box>
            </Box>
          </Box>
        </Card>
      </Box>
      {/* ===== Split‑screen ===== */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          overflow: "hidden",
          flexDirection: { xs: "column", md: "row" },
          pt: 0,
          transition: "padding-top 0.3s ease-in-out",
        }}
      >
        {/* left: passage */}
        <Box
          sx={{
            width: { xs: "100%", md: "50%" },
            height: { xs: "50%", md: "100%" },
            p: { xs: 1, sm: 2 },
            overflow: "auto",
            borderRight: { md: "2px solid #e0e0e0" },
            borderBottom: { xs: "2px solid #e0e0e0", md: "none" },
            background: "#fff",
            position: "relative",
          }}
        >
          {/* Zoom Controls - Left Panel */}
          <Box
            sx={{
              position: "sticky",
              top: 8,
              right: 8,
              zIndex: 1000,
              display: "flex",
              justifyContent: "flex-end",
              mb: 1,
              gap: 1,
            }}
          >
            <Tooltip title="Zoom Out" placement="top">
              <IconButton
                onClick={handleLeftZoomOut}
                disabled={leftZoom <= 0.8}
                sx={{
                  width: 38,
                  height: 38,
                  bgcolor: "rgba(157, 34, 53, 0.08)",
                  backdropFilter: "blur(10px)",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    bgcolor: "rgba(157, 34, 53, 0.15)",
                    transform: "scale(1.05)",
                  },
                  "&:disabled": {
                    bgcolor: "rgba(157, 34, 53, 0.03)",
                    opacity: 0.5,
                  },
                }}
              >
                <ZoomOut sx={{ fontSize: 18, color: "#9D2235" }} />
              </IconButton>
            </Tooltip>
            <Typography
              sx={{
                alignSelf: "center",
                fontSize: "0.875rem",
                fontWeight: 600,
                color: "#666",
                minWidth: "50px",
                textAlign: "center",
                bgcolor: "rgba(157, 34, 53, 0.05)",
                borderRadius: 1,
                px: 1,
                py: 0.5,
              }}
            >
              {Math.round(leftZoom * 100)}%
            </Typography>
            <Tooltip title="Zoom In" placement="top">
              <IconButton
                onClick={handleLeftZoomIn}
                disabled={leftZoom >= 1.5}
                sx={{
                  width: 38,
                  height: 38,
                  bgcolor: "rgba(157, 34, 53, 0.08)",
                  backdropFilter: "blur(10px)",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    bgcolor: "rgba(157, 34, 53, 0.15)",
                    transform: "scale(1.05)",
                  },
                  "&:disabled": {
                    bgcolor: "rgba(157, 34, 53, 0.03)",
                    opacity: 0.5,
                  },
                }}
              >
                <ZoomIn sx={{ fontSize: 18, color: "#9D2235" }} />
              </IconButton>
            </Tooltip>
          </Box>
          <Box
            sx={{
              transform: `scale(${leftZoom})`,
              transformOrigin: "top left",
              transition: "transform 0.2s ease",
            }}
          >
            <TextHighlighterWrapper onAddNote={onAddNote}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: "bold",
                  mb: { xs: 1, sm: 2 },
                  fontSize: { xs: "1rem", sm: "1.25rem" },
                }}
              >
                Reading Passage
              </Typography>
              {extracts.map((txt, i) => (
                <Card
                  key={i}
                  elevation={2}
                  sx={{
                    mb: { xs: 2, sm: 3 },
                    p: { xs: 1.5, sm: 3 },
                    borderRadius: 2,
                    boxShadow: "0px 4px 12px rgba(0,0,0,0.08)",
                    background: "#fff",
                  }}
                >
                  {txt.title && (
                    <HtmlRenderer
                      content={txt.title}
                      variant="h6"
                      sx={{
                        fontWeight: 700,
                        mb: { xs: 1.5, sm: 2 },
                        textAlign: "center",
                        color: "#222",
                        fontSize: { xs: "1rem", sm: "1.25rem" },
                      }}
                    />
                  )}
                  {txt.paragraphs?.map((p, pIdx) => (
                    <HtmlRenderer
                      key={pIdx}
                      content={p}
                      component="div"
                      sx={{
                        mb: { xs: 1.5, sm: 2 },
                        lineHeight: { xs: 1.6, sm: 1.8 },
                        whiteSpace: "normal",
                        textAlign: "justify",
                        fontSize: { xs: "0.875rem", sm: "1rem" },
                        wordWrap: "break-word",
                        overflowWrap: "break-word",
                      }}
                    />
                  ))}
                  {txt.imageUrl && (
                    <Box textAlign="center" sx={{ mb: { xs: 1.5, sm: 2 } }}>
                      <img
                        src={`${txt.imageUrl}`}
                        alt={txt.title || "Reading image"}
                        style={{ maxWidth: "100%", height: "auto" }}
                      />
                    </Box>
                  )}
                  {txt.tables && (
                    <>
                      {txt.tables.mainHeading && (
                        <HtmlRenderer
                          content={txt.tables.mainHeading}
                          component="h6"
                          sx={{
                            mb: { xs: 1, sm: 1.5 },
                            fontWeight: 600,
                            fontSize: { xs: "1rem", sm: "1.125rem" },
                          }}
                        />
                      )}
                      {txt.tables.instructions && (
                        <HtmlRenderer
                          content={txt.tables.instructions}
                          component="div"
                          sx={{
                            mb: { xs: 1, sm: 1.5 },
                            fontSize: { xs: "0.875rem", sm: "1rem" },
                          }}
                        />
                      )}
                      <TableContainer
                        component={Paper}
                        sx={{
                          mb: { xs: 1.5, sm: 2 },
                          overflowX: "auto",
                        }}
                      >
                        <Table
                          size={window.innerWidth < 600 ? "small" : "medium"}
                        >
                          <TableHead>
                            <TableRow>
                              {txt.tables.columns?.map((h, hIdx) => (
                                <TableCell
                                  key={hIdx}
                                  sx={{
                                    fontSize: { xs: "0.75rem", sm: "0.875rem" },
                                    fontWeight: 600,
                                  }}
                                >
                                  <HtmlRenderer
                                    content={h}
                                    component="span"
                                    sx={{
                                      fontSize: "inherit",
                                      fontWeight: "inherit",
                                    }}
                                  />
                                </TableCell>
                              ))}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {txt.tables.rows?.map((row, rIdx) => (
                              <TableRow key={rIdx}>
                                {row.map((cell, cIdx) => (
                                  <TableCell
                                    key={cIdx}
                                    sx={{
                                      fontSize: {
                                        xs: "0.75rem",
                                        sm: "0.875rem",
                                      },
                                    }}
                                  >
                                    <HtmlRenderer
                                      content={cell}
                                      component="span"
                                      sx={{
                                        fontSize: "inherit",
                                      }}
                                    />
                                  </TableCell>
                                ))}
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </>
                  )}
                </Card>
              ))}
            </TextHighlighterWrapper>
          </Box>
        </Box>
        {/* right: questions */}
        <Box
          ref={rightPanelRef}
          sx={{
            width: { xs: "100%", md: "50%" },
            height: { xs: "50%", md: "100%" },
            p: { xs: 1, sm: 2 },
            overflow: "auto",
            background: "#f8f9fa",
            position: "relative",
          }}
        >
          {/* Zoom Controls - Right Panel */}
          <Box
            sx={{
              position: "sticky",
              top: 8,
              right: 8,
              zIndex: 1000,
              display: "flex",
              justifyContent: "flex-end",
              mb: 1,
              gap: 1,
            }}
          >
            <Tooltip title="Zoom Out" placement="top">
              <IconButton
                onClick={handleRightZoomOut}
                disabled={rightZoom <= 0.8}
                sx={{
                  width: 38,
                  height: 38,
                  bgcolor: "rgba(157, 34, 53, 0.08)",
                  backdropFilter: "blur(10px)",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    bgcolor: "rgba(157, 34, 53, 0.15)",
                    transform: "scale(1.05)",
                  },
                  "&:disabled": {
                    bgcolor: "rgba(157, 34, 53, 0.03)",
                    opacity: 0.5,
                  },
                }}
              >
                <ZoomOut sx={{ fontSize: 18, color: "#9D2235" }} />
              </IconButton>
            </Tooltip>
            <Typography
              sx={{
                alignSelf: "center",
                fontSize: "0.875rem",
                fontWeight: 600,
                color: "#666",
                minWidth: "50px",
                textAlign: "center",
                bgcolor: "rgba(157, 34, 53, 0.05)",
                borderRadius: 1,
                px: 1,
                py: 0.5,
              }}
            >
              {Math.round(rightZoom * 100)}%
            </Typography>
            <Tooltip title="Zoom In" placement="top">
              <IconButton
                onClick={handleRightZoomIn}
                disabled={rightZoom >= 1.5}
                sx={{
                  width: 38,
                  height: 38,
                  bgcolor: "rgba(157, 34, 53, 0.08)",
                  backdropFilter: "blur(10px)",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    bgcolor: "rgba(157, 34, 53, 0.15)",
                    transform: "scale(1.05)",
                  },
                  "&:disabled": {
                    bgcolor: "rgba(157, 34, 53, 0.03)",
                    opacity: 0.5,
                  },
                }}
              >
                <ZoomIn sx={{ fontSize: 18, color: "#9D2235" }} />
              </IconButton>
            </Tooltip>
          </Box>
          <Box
            sx={{
              zoom: rightZoom,
              transition: "zoom 0.2s ease",
            }}
          >
            <TextHighlighterWrapper onAddNote={onAddNote}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: "bold",
                  mb: { xs: 1, sm: 2 },
                  fontSize: { xs: "1rem", sm: "1.25rem" },
                }}
              >
                Questions
              </Typography>
              {/* MCQs */}
              {part.mcqQuestions?.map((q, idx) => (
                <TextHighlighterWrapper
                  key={q.question_id}
                  onAddNote={onAddNote}
                >
                  <Box
                    sx={{ mb: { xs: 1.5, sm: 2 } }}
                    data-question-id={q.question_id}
                  >
                    <MockMcq
                      mcqData={q}
                      selectedValue={(() => {
                        // For Part A dropdown, we need to convert stored full text back to option key for display
                        if (currentPart === "A" && answers[q.question_id]) {
                          const storedAnswer = answers[q.question_id];
                          // Find the option that matches the stored text
                          const optionIndex = q.options.findIndex((opt) => {
                            const optionText =
                              opt?.text ?? opt?.value ?? opt?.label ?? opt;
                            return optionText === storedAnswer;
                          });
                          if (optionIndex !== -1) {
                            const option = q.options[optionIndex];
                            return (
                              option?.key ??
                              String.fromCharCode(65 + optionIndex)
                            );
                          }
                          // If no exact match found, check if the stored answer is already a key
                          if (/^[A-D]$/.test(storedAnswer)) {
                            return storedAnswer;
                          }
                        }
                        return answers[q.question_id] || "";
                      })()}
                      handleAnswerChange={(qid, value) => {
                        if (currentPart === "A") {
                          const optionObj = q.options.find(
                            (opt, optIdx) =>
                              (opt.key ?? String.fromCharCode(65 + optIdx)) ===
                              value,
                          );
                          const fullText =
                            optionObj?.text ??
                            optionObj?.value ??
                            optionObj?.label ??
                            optionObj ??
                            value;
                          onAnswerChange(qid, fullText);
                        } else {
                          onAnswerChange(qid, value);
                        }
                      }}
                      partKey={partKey}
                      questionNumber={q.questionId}
                      asDropdown={currentPart === "A"}
                      dropdownContainer={rightPanelRef.current}
                    />
                  </Box>
                </TextHighlighterWrapper>
              ))}
              {/* sentence completion */}
              {part.sentenceCompletionQuestions?.length > 0 && (
                <TextHighlighterWrapper onAddNote={onAddNote}>
                  <Box sx={{ mb: { xs: 2, sm: 3 } }}>
                    <Typography
                      variant="h6"
                      sx={{
                        mb: { xs: 0.75, sm: 1 },
                        fontSize: { xs: "1rem", sm: "1.25rem" },
                      }}
                    >
                      Short Answer Questions
                    </Typography>
                    <MockNoteCompletion
                      noteCompletionData={{
                        sections: [
                          {
                            heading: "",
                            notes: part.sentenceCompletionQuestions.map(
                              (q, idx) => ({
                                questionId: q.question_id,
                                text: `(${
                                  questionBase +
                                  part.mcqQuestions.length +
                                  idx +
                                  1
                                }) ${q.question}`,
                                hasBlank: true,
                              }),
                            ),
                          },
                        ],
                      }}
                      handleAnswerChange={onAnswerChange}
                      answers={answers}
                    />
                  </Box>
                </TextHighlighterWrapper>
              )}{" "}
              {/* note completion */}
              {part.noteCompletionQuestions?.length > 0 && (
                <TextHighlighterWrapper onAddNote={onAddNote}>
                  <Box sx={{ mb: { xs: 2, sm: 3 } }}>
                    <Typography
                      variant="h6"
                      sx={{
                        mb: { xs: 0.75, sm: 1 },
                        fontSize: { xs: "1rem", sm: "1.25rem" },
                      }}
                    >
                      Complete the sentences
                    </Typography>
                    <MockNoteCompletion
                      noteCompletionData={{
                        sections: [
                          {
                            heading: "",
                            notes: part.noteCompletionQuestions.map(
                              (q, idx) => ({
                                questionId: q.question_id,
                                text: `(${
                                  questionBase +
                                  part.mcqQuestions.length +
                                  part.sentenceCompletionQuestions.length +
                                  idx +
                                  1
                                }) ${q.question}`,
                                hasBlank: true,
                              }),
                            ),
                          },
                        ],
                      }}
                      handleAnswerChange={onAnswerChange}
                      answers={answers}
                    />
                  </Box>
                </TextHighlighterWrapper>
              )}
            </TextHighlighterWrapper>
          </Box>
        </Box>
      </Box>{" "}
      {/* FOOTER */}
      <Box
        sx={{
          bgcolor: "#fff",
          borderTop: "1px solid #e0e0e0",
          px: { xs: 1, sm: 4 },
          py: { xs: 1.5, sm: 2 },
          position: "sticky",
          bottom: 0,
          zIndex: 10,
          width: "100%",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        {showBackButton && (
          <Button
            variant="outlined"
            onClick={handleBackClick}
            sx={{
              minWidth: { xs: 80, sm: 100 },
              textTransform: "none",
              borderColor: "#9D2235",
              color: "#9D2235",
              py: { xs: 0.75, sm: 1 },
              px: { xs: 1.5, sm: 2 },
              borderRadius: 3,
              fontSize: { xs: "0.875rem", sm: "1rem" },
              "&:hover": {
                borderColor: "#9D2235",
                backgroundColor: "rgba(157, 34, 53, 0.04)",
              },
            }}
          >
            Back
          </Button>
        )}
        {!showBackButton && <Box />}
        <Button
          variant="contained"
          size="large"
          onClick={handleNextClick}
          sx={{
            minWidth: { xs: 120, sm: 140 },
            textTransform: "none",
            bgcolor: "#9D2235",
            py: { xs: 0.75, sm: 1 },
            px: { xs: 2, sm: 3 },
            borderRadius: 3,
            fontSize: { xs: "0.875rem", sm: "1rem" },
          }}
        >
          {isLastQuestion ? "Finish" : getButtonText()}
        </Button>
      </Box>
      {/* Notes Modal */}
      <NotesModal
        open={notesModalOpen}
        onClose={() => setNotesModalOpen(false)}
        notes={notes}
      />
      {/* TIMER EXPIRED MODAL */}
      <Dialog open={timerExpired}>
        <DialogContent>
          <Typography variant="h6" textAlign="center" gutterBottom>
            Time's up!
          </Typography>
          <Typography textAlign="center" sx={{ mb: 2 }}>
            {currentPart === "A"
              ? `Moving to next part in ${expiryCountdown} seconds.`
              : `The test will be submitted in ${expiryCountdown} seconds.`}
          </Typography>
          <Box textAlign="center" sx={{ mb: 2 }}>
            <Typography
              variant="h1"
              sx={{
                fontWeight: 800,
                fontSize: { xs: 48, sm: 72 },
                color: "#9D2235",
              }}
            >
              {expiryCountdown}
            </Typography>
            <Typography variant="subtitle1" sx={{ mt: -1 }}>
              seconds
            </Typography>
          </Box>
          <Box textAlign="center">
            <Button
              variant="contained"
              color="secondary"
              onClick={handleTimerContinue}
              sx={{ bgcolor: "#9D2235", mt: 1 }}
            >
              Continue
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
      {/* CONFIRM NAVIGATION MODAL */}
      <Dialog open={showConfirm} onClose={handleConfirmNo}>
        <DialogContent>
          <DialogTitle sx={{ textAlign: "center", pt: 3 }}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 2,
              }}
            >
              <Warning color="warning" sx={{ fontSize: 50 }} />
              <Typography variant="h6" fontWeight="bold">
                Unattempted Question – Confirm Section Completion
              </Typography>
            </Box>
          </DialogTitle>
          <DialogContent sx={{ pt: 2, pb: 3 }}>
            <Typography variant="body1" paragraph>
              Are you sure you want to move to the next section without
              completing question?
            </Typography>

            <Typography variant="body1" fontWeight="bold">
              Are you sure you want to finish this section?
            </Typography>
          </DialogContent>
          <Box mt={2} display="flex" justifyContent="center" gap={2}>
            <Button variant="outlined" onClick={handleConfirmNo}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleConfirmYes}
              sx={{ bgcolor: "#9D2235" }}
            >
              Yes, Continue
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
}
